/* Liquid glass for the header pill — kube.io's "Liquid Glass in CSS and SVG"
   (kube.io/blog/liquid-glass-css-svg, 2025) ported 1:1 and applied to
   .site-header::before. One SVG filter, in the article's shipped order: blur
   the backdrop, refract it through a displacement map computed from a
   convex-squircle bezel with Snell's law, then lay its rim-light specular map
   on top. The two maps are rasterised here at the pill's size, exactly as the
   article's generator does (its shipped PNGs were reproduced bit for bit from
   this math). Chromium only: `backdrop-filter: url()` renders in no other
   engine, and WebKit PARSES it, so the gate is the engine itself —
   navigator.vendor is "Google Inc." for every Blink browser and
   "Apple Computer, Inc." for every WebKit one, iOS browsers included — never
   the user-agent string. Other engines keep the plain blur in
   component-site-header.css. Knobs live in SB_GLASS below. */
(function () {
  'use strict';
  if (typeof navigator === 'undefined' || navigator.vendor !== 'Google Inc.') return;
  if (!window.CSS || !CSS.supports('backdrop-filter', 'url(#x)')) return;
  if (!('ResizeObserver' in window) || typeof ImageData === 'undefined') return;
  var pill = document.querySelector('.site-header');
  if (!pill) return;

  /* ---------- knobs ----------
     Geometry follows the article's search pill (320×42: bezel 18, thickness
     100, n 1.3, squircle) scaled to our taller pill: bezel ≈ 0.43 × height,
     always below the corner radius (bezel == radius is a divide-by-zero in
     the rasteriser; bezel > radius truncates to the radius). The bezel is
     read from --sb-glass-bezel so CSS can size it per breakpoint. */
  var SB_GLASS = {
    profile: 'convex_squircle',
    refractiveIndex: 1.3,
    glassThickness: 100,
    samples: 128,
    scaleRatio: 1,          // feDisplacementMap scale = maximumDisplacement × this
    blur: 10,               // feGaussianBlur stdDeviation on the backdrop, BEFORE refraction (the article's demos use 1 with a 60% tint)
    specularOpacity: 0.2,   // searchbox demo values — the rim light is 2 CSS px wide, grey, lit from 60°
    specularSaturation: 4,
    lightAngle: Math.PI / 3
  };

  /* ---------- 1. surface profiles (bundle 2668-2679) ---------- */
  var profiles = {
    convex_circle: function (x) { return Math.sqrt(1 - Math.pow(1 - x, 2)); },
    convex_squircle: function (x) { return Math.pow(1 - Math.pow(1 - x, 4), 1 / 4); },
    concave: function (x) { return 1 - Math.sqrt(1 - Math.pow(1 - x, 2)); },
    lip: function (x) {
      var t = Math.pow(1 - Math.pow(1 - x * 2, 4), 1 / 4);
      var n = (1 - Math.sqrt(1 - Math.pow(1 - x, 2))) + 0.1;
      var r = 6 * Math.pow(x, 5) - 15 * Math.pow(x, 4) + 10 * Math.pow(x, 3);
      return t * (1 - r) + n * r;
    }
  };

  /* ---------- 2. rays: displacement per normalised distance from the border (bundle Zt, 2839-2854) ----------
     Single refraction at the top surface (ambient index 1, eta = 1/n), the ray
     comes straight down, then runs (height × bezelWidth + glassThickness) to
     the background. Positive = toward the centre. Size-independent. */
  function computeBezelDisplacement(glassThickness, bezelWidth, profile, refractiveIndex, samples) {
    var a = 1 / refractiveIndex;
    function refract(nx, ny) {                       // GLSL refract(I=(0,1), N, eta)
      var k = 1 - a * a * (1 - ny * ny);
      if (k < 0) return null;
      var sq = Math.sqrt(k);
      return [-(a * ny + sq) * nx, a - (a * ny + sq) * ny];
    }
    var out = new Array(samples);
    for (var i = 0; i < samples; i++) {
      var s = i / samples;
      var c = profile(s);
      var l = s < 1 ? 1e-4 : -1e-4;                  // one-sided finite difference, as the bundle does
      var u = (profile(s + l) - c) / l;
      var d = Math.sqrt(u * u + 1);
      var p = refract(-u / d, -1 / d);
      out[i] = p ? p[0] * ((c * bezelWidth + glassThickness) / p[1]) : 0;
    }
    return out;
  }
  function maxAbs(arr) { var m = 0; for (var i = 0; i < arr.length; i++) { var v = Math.abs(arr[i]); if (v > m) m = v; } return m; }

  /* ---------- 3. displacement map rasteriser (bundle Qt, 2855-2869) ----------
     CSS px in, device px out. Corners are quarter circles (radial normal),
     straight runs are edge lines (axis-aligned normal). R = 128 + dx·127·aa,
     G = 128 + dy·127·aa, B = 0, A = 255; neutral (128,128,0,255) elsewhere. */
  function rasterizeDisplacementMap(elementW, elementH, radius, bezelWidth, maximumDisplacement, samples, dpr) {
    var l = dpr;
    var u = elementW * l, dd0 = elementH * l;
    var data = new Uint8ClampedArray(u * dd0 * 4);
    for (var k = 0; k < data.length; k += 4) { data[k] = 128; data[k + 1] = 128; data[k + 2] = 0; data[k + 3] = 255; }
    var p = radius * l, m = bezelWidth * l;
    var h = p * p, g = (p + 1) * (p + 1), _ = (p - m) * (p - m);
    var v = u, y = dd0;
    var b = v - p * 2, ee = y - p * 2;
    var N = samples.length;
    for (var e = 0; e < y; e++) {
      for (var t = 0; t < v; t++) {
        var n = (e * u + t) * 4;
        var r = t < p, i = t >= v - p, a = e < p, c = e >= y - p;
        var ll = r ? t - p : i ? t - p - b : 0;
        var dd = a ? e - p : c ? e - p - ee : 0;
        var S = ll * ll + dd * dd;
        if (S <= g && S >= _) {
          var aa = S < h ? 1 : 1 - (Math.sqrt(S) - Math.sqrt(h)) / (Math.sqrt(g) - Math.sqrt(h));
          var dist = Math.sqrt(S);
          var fromBorder = p - dist;
          var nx = ll / dist, ny = dd / dist;
          var cc = samples[(fromBorder / m * N) | 0];
          if (cc === undefined) cc = 0;
          var dx = -nx * cc / maximumDisplacement, dy = -ny * cc / maximumDisplacement;
          data[n] = 128 + dx * 127 * aa;
          data[n + 1] = 128 + dy * 127 * aa;
          data[n + 2] = 0;
          data[n + 3] = 255;
        }
      }
    }
    return { width: u, height: dd0, data: data };
  }

  /* ---------- 4. specular rim map (bundle Yr, 5236-5250) ----------
     A grey ramp, always ~2 CSS px wide, alpha ∝ |n·L|² — the top and bottom
     runs are lit at 60°, the caps are dim. Transparent elsewhere. */
  function rasterizeSpecularMap(width, height, radius, specularWidth, lightAngle, dpr) {
    var o = dpr;
    var s = width * o, c = height * o;
    var data = new Uint8ClampedArray(s * c * 4);
    var u = radius * o, d = specularWidth * o;
    var f = [Math.cos(lightAngle), Math.sin(lightAngle)];
    var p = u * u, m = (u + o) * (u + o), h = (u - d) * (u - d);
    var g = s - u * 2, _ = c - u * 2;
    for (var e = 0; e < c; e++) {
      for (var t = 0; t < s; t++) {
        var n = (e * s + t) * 4;
        var r = t < u, i = t >= s - u, a = e < u, dd = e >= c - u;
        var v = r ? t - u : i ? t - u - g : 0;
        var y = a ? e - u : dd ? e - u - _ : 0;
        var b = v * v + y * y;
        if (b <= m && b >= h) {
          var dist = Math.sqrt(b);
          var fromBorder = u - dist;
          var aa = b < p ? 1 : 1 - (dist - Math.sqrt(p)) / (Math.sqrt(m) - Math.sqrt(p));
          var nx = v / dist, ny = -y / dist;
          var str = Math.abs(nx * f[0] + ny * f[1]) * Math.sqrt(1 - Math.pow(1 - fromBorder / (1 * o), 2));
          var cc = 255 * str;
          data[n] = cc; data[n + 1] = cc; data[n + 2] = cc; data[n + 3] = cc * str * aa;
        }
      }
    }
    return { width: s, height: c, data: data };
  }

  /* ---------- 5. ImageData → data URL (bundle fr, 4358-4365) ---------- */
  function toDataUrl(img) {
    var canvas = document.createElement('canvas');
    canvas.width = img.width; canvas.height = img.height;
    var ctx = canvas.getContext('2d');
    ctx.putImageData(new ImageData(img.data, img.width, img.height), 0, 0);
    return canvas.toDataURL();
  }

  /* ---------- 6. the filter, attribute for attribute (bundle Cn, 3277-3304) ----------
     Inserted once as the pill's first child, exactly where the article puts
     it; only the two hrefs, the two feImage boxes and the scale change on
     resize. color-interpolation-filters="sRGB" is load-bearing: under
     linearRGB the neutral 128 would shift the whole backdrop. */
  var ID = 'sb-liquid-glass';
  var host = document.createElement('div');
  host.innerHTML =
    '<svg xmlns="http://www.w3.org/2000/svg" color-interpolation-filters="sRGB" style="display:none" aria-hidden="true"><defs>' +
    '<filter id="' + ID + '">' +
      '<feGaussianBlur in="SourceGraphic" stdDeviation="' + SB_GLASS.blur + '" result="blurred_source"/>' +
      '<feImage x="0" y="0" width="1" height="1" result="displacement_map"/>' +
      '<feDisplacementMap in="blurred_source" in2="displacement_map" scale="0" xChannelSelector="R" yChannelSelector="G" result="displaced"/>' +
      '<feColorMatrix in="displaced" type="saturate" values="' + SB_GLASS.specularSaturation + '" result="displaced_saturated"/>' +
      '<feImage x="0" y="0" width="1" height="1" result="specular_layer"/>' +
      '<feComposite in="displaced_saturated" in2="specular_layer" operator="in" result="specular_saturated"/>' +
      '<feComponentTransfer in="specular_layer" result="specular_faded"><feFuncA type="linear" slope="' + SB_GLASS.specularOpacity + '"/></feComponentTransfer>' +
      '<feBlend in="specular_saturated" in2="displaced" mode="normal" result="withSaturation"/>' +
      '<feBlend in="specular_faded" in2="withSaturation" mode="normal"/>' +
    '</filter></defs></svg>';
  var svg = host.firstChild;
  pill.insertBefore(svg, pill.firstChild);
  var images = svg.querySelectorAll('feImage');
  var dispImg = images[0], specImg = images[1];
  var dmap = svg.querySelector('feDisplacementMap');

  function bezelFor(h) {
    var css = parseFloat(getComputedStyle(pill).getPropertyValue('--sb-glass-bezel'));
    var bezel = css > 0 ? css : Math.round(h * 0.43);
    var radius = h / 2;
    if (bezel >= radius) bezel = Math.max(1, Math.floor(radius) - 1);   // never equal to the radius
    return bezel;
  }

  var last = '', timer = 0;
  function draw() {
    // the pane is ::before with inset:0 → the pill's padding box
    var w = Math.round(pill.clientWidth), h = Math.round(pill.clientHeight);
    if (w < 2 || h < 2) return;
    var dpr = Math.min(3, Math.max(1, Math.round(window.devicePixelRatio || 1)));
    var bezel = bezelFor(h);
    var key = w + 'x' + h + '@' + dpr + '/' + bezel;
    if (key === last) return;
    last = key;
    var radius = h / 2;
    var samples = computeBezelDisplacement(SB_GLASS.glassThickness, bezel, profiles[SB_GLASS.profile], SB_GLASS.refractiveIndex, SB_GLASS.samples);
    var maximumDisplacement = maxAbs(samples) || 1;
    var disp = rasterizeDisplacementMap(w, h, radius, bezel, maximumDisplacement, samples, dpr);
    var spec = rasterizeSpecularMap(w, h, radius, 2, SB_GLASS.lightAngle, dpr);
    dispImg.setAttribute('href', toDataUrl(disp)); dispImg.setAttribute('width', w); dispImg.setAttribute('height', h);
    specImg.setAttribute('href', toDataUrl(spec)); specImg.setAttribute('width', w); specImg.setAttribute('height', h);
    dmap.setAttribute('scale', String(maximumDisplacement * SB_GLASS.scaleRatio));
    document.documentElement.classList.add('sb-lens-ok');
  }
  new ResizeObserver(function () { clearTimeout(timer); timer = setTimeout(draw, 120); }).observe(pill);
  draw();
})();
